import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Moon, Eye, Heart, CircleDot } from "lucide-react";
import { Reveal, Overline } from "./Reveal";

const RINGS =
  "https://images.pexels.com/photos/7182162/pexels-photo-7182162.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const archetypes = [
  {
    id: "persona",
    label: "La Persona",
    icon: Eye,
    title: "La maschera che mostriamo al mondo",
    text: "La Persona è il volto che presentiamo agli altri: ruoli, abitudini, aspettative sociali. Quando ci identifichiamo troppo con essa, perdiamo il contatto con ciò che siamo davvero. Il lavoro analitico aiuta a distinguere la maschera dal volto.",
  },
  {
    id: "ombra",
    label: "L'Ombra",
    icon: Moon,
    title: "Ciò che di noi non vogliamo vedere",
    text: "L'Ombra raccoglie le parti di noi che abbiamo rimosso: paure, desideri, rabbia, ma anche risorse e talenti inespressi. Riconoscerla non significa farsene travolgere, ma restituirle dignità — è il primo passo verso l'integrità.",
  },
  {
    id: "anima",
    label: "Anima e Animus",
    icon: Heart,
    title: "Le immagini interiori dell'altro",
    text: "Anima e Animus sono le figure interiori che plasmano il nostro modo di amare, scegliere e relazionarci. Renderle consapevoli significa smettere di proiettarle sugli altri e iniziare a incontrarle dentro di sé.",
  },
  {
    id: "se",
    label: "Il Sé",
    icon: CircleDot,
    title: "Il centro che orienta il cammino",
    text: "Il Sé è l'archetipo della totalità: il centro ordinatore della psiche, spesso raffigurato come un mandala. Il processo di individuazione è il cammino — lungo una vita — verso questo centro, verso ciò che siamo chiamati a diventare.",
  },
];

const pillars = [
  { title: "Analisi dei sogni", text: "Il sogno come lettera dell'inconscio: un linguaggio simbolico che orienta il percorso." },
  { title: "Immaginazione attiva", text: "Dialogare con le immagini interiori per dare forma a ciò che non ha ancora parole." },
  { title: "Lavoro simbolico", text: "Riconoscere nei sintomi e nelle ripetizioni i simboli che chiedono trasformazione." },
  { title: "Individuazione", text: "Il percorso verso sé stessi: diventare ciò che si è, in profondità." },
];

export const Approccio = () => {
  const [active, setActive] = useState(archetypes[0]);

  return (
    <section id="approccio" data-testid="approccio-section" className="py-24 sm:py-32 lg:py-40 bg-ink-soft/40 border-y border-gold/10 relative overflow-hidden">
      <div
        className="absolute right-[-10%] top-[-10%] w-[520px] h-[520px] rounded-full opacity-[0.07] pointer-events-none mandala-spin"
        style={{ backgroundImage: `url(${RINGS})`, backgroundSize: "cover" }}
      />
      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto relative">
        <Reveal>
          <Overline>L'approccio junghiano</Overline>
          <h2 className="font-serif tracking-tight leading-snug text-3xl sm:text-4xl lg:text-5xl text-sand max-w-3xl">
            La psicologia del <span className="italic text-gold">profondo</span>
          </h2>
          <p className="mt-7 max-w-2xl text-base sm:text-lg text-sand-muted leading-relaxed">
            Per Jung la psiche non è un problema da risolvere, ma un territorio da abitare. La
            psicologia analitica lavora con i simboli, i sogni e gli archetipi — le forme
            universali che attraversano la storia di ognuno di noi.
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-5" delay={0.1}>
            <div className="flex flex-col gap-2" role="tablist" aria-label="Archetipi">
              {archetypes.map((a) => {
                const Icon = a.icon;
                const isActive = active.id === a.id;
                return (
                  <button
                    key={a.id}
                    role="tab"
                    aria-selected={isActive}
                    data-testid={`archetype-tab-${a.id}`}
                    onClick={() => setActive(a)}
                    className={`group flex items-center gap-4 text-left px-6 py-5 rounded-xl border transition-all duration-500 ${
                      isActive
                        ? "bg-ink-elevated border-gold/40"
                        : "bg-transparent border-gold/10 hover:border-gold/30 hover:bg-ink-elevated/50"
                    }`}
                  >
                    <Icon
                      size={20}
                      className={isActive ? "text-gold" : "text-sand-faint group-hover:text-gold transition-colors"}
                    />
                    <span className={`font-serif text-lg ${isActive ? "text-gold" : "text-sand-muted group-hover:text-sand"} transition-colors`}>
                      {a.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </Reveal>

          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                data-testid="archetype-panel"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="h-full bg-ink-elevated border border-gold/20 rounded-2xl p-8 sm:p-12 flex flex-col justify-center relative overflow-hidden"
              >
                <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full border border-gold/15" />
                <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full border border-gold/25" />
                <h3 className="font-serif text-2xl sm:text-3xl text-gold">{active.title}</h3>
                <p className="mt-6 text-base sm:text-lg text-sand-muted leading-relaxed">{active.text}</p>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="pillars-grid">
          {pillars.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <div className="group h-full bg-ink-soft border border-gold/10 hover:border-gold/35 rounded-xl p-7 transition-all duration-500 hover:-translate-y-1.5">
                <p className="font-cormorant italic text-4xl text-gold/40 group-hover:text-gold transition-colors duration-500">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h4 className="font-sans font-medium text-lg text-sand mt-4">{p.title}</h4>
                <p className="text-sm text-sand-muted mt-3 leading-relaxed">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
